import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { ArrowLeft, Save } from 'lucide-react';
import { Form, Button, Card, Container, Row, Col } from 'react-bootstrap';
import { toUpperCase } from '../utils/formatters';
import SchedulingCalendar from './SchedulingCalendar';

interface Professional {
    id: number;
    name: string;
}

const AgendaForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [formData, setFormData] = useState({
        name: '',
        professional: '',
        start_date: '',
        end_date: '',
        slot_duration: '00:30:00',
        start_time: '09:00:00',
        end_time: '17:00:00',
        lunch_start_time: '',
        lunch_end_time: '',
        validate_slots: true,
        open_ended: false,
        allow_holidays: true,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchProfessionals();
        if (id) {
            fetchAgenda();
        }
    }, [id]);

    const fetchProfessionals = async () => {
        try {
            const response = await api.get('/professionals/');
            setProfessionals(response.data);
        } catch (error) {
            console.error('Error fetching professionals:', error);
        }
    };

    const fetchAgenda = async () => {
        try {
            const response = await api.get(`/agendas/${id}/`);
            setFormData(response.data);
        } catch (error) {
            console.error('Error fetching agenda:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                start_date: formData.open_ended ? null : (formData.start_date || null),
                end_date: formData.open_ended ? null : (formData.end_date || null)
            };

            if (id) {
                await api.put(`/agendas/${id}/`, payload);
            } else {
                await api.post('/agendas/', payload);
            }
            navigate(`/professionals/${formData.professional}`);
        } catch (error) {
            console.error('Error saving agenda:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-2">
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate(-1)}
                    className="text-decoration-none text-secondary p-0 me-3 d-flex align-items-center gap-2"
                >
                    <ArrowLeft size={20} /> Voltar
                </Button>
                <h2 className="mb-0">{id ? 'Editar Agenda' : 'Nova Agenda'}</h2>
            </div>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={12}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome da Agenda</Form.Label>
                                            <Form.Control
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: toUpperCase(e.target.value) })}
                                                required
                                                placeholder="Ex: Agenda Dezembro 2025"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Profissional</Form.Label>
                                            <Form.Select
                                                value={formData.professional}
                                                onChange={(e) => setFormData({ ...formData, professional: e.target.value })}
                                                required
                                            >
                                                <option value="">Selecione um profissional</option>
                                                {professionals.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {!formData.open_ended && (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Data Início</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={formData.start_date}
                                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                                    required={!formData.open_ended}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Data Fim</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={formData.end_date}
                                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                                    required={!formData.open_ended}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Intervalo (HH:MM:SS)</Form.Label>
                                            <Form.Control
                                                value={formData.slot_duration}
                                                onChange={(e) => setFormData({ ...formData, slot_duration: e.target.value })}
                                                required
                                                placeholder="00:30:00"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Hora Início</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={formData.start_time}
                                                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Hora Fim</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={formData.end_time}
                                                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={12}>
                                        <h6 className="mt-2 mb-3 text-secondary">Horário de Almoço (Opcional)</h6>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Início Almoço</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={formData.lunch_start_time}
                                                onChange={(e) => setFormData({ ...formData, lunch_start_time: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Fim Almoço</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={formData.lunch_end_time}
                                                onChange={(e) => setFormData({ ...formData, lunch_end_time: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold mb-3 d-block">Dias da Semana</Form.Label>
                                    <div className="d-flex flex-nowrap gap-1">
                                        {[
                                            { id: 'sunday', label: 'Dom' },
                                            { id: 'monday', label: 'Seg' },
                                            { id: 'tuesday', label: 'Ter' },
                                            { id: 'wednesday', label: 'Qua' },
                                            { id: 'thursday', label: 'Qui' },
                                            { id: 'friday', label: 'Sex' },
                                            { id: 'saturday', label: 'Sáb' },
                                        ].map(day => {
                                            const isChecked = (formData as any)[day.id];
                                            return (
                                                <div
                                                    key={day.id}
                                                    className={`flex-fill text-center py-2 border rounded ${isChecked ? 'bg-primary text-white border-primary' : 'bg-white text-muted'}`}
                                                    style={{ cursor: 'pointer', minWidth: 0, fontSize: '0.8rem' }}
                                                    onClick={() => setFormData({ ...formData, [day.id]: !isChecked })}
                                                >
                                                    {isChecked && <span className="me-1">✓</span>}
                                                    <span className="fw-medium">{day.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <div className="bg-light p-3 rounded">
                                    <h6 className="mb-3 text-secondary">Configurações Avançadas</h6>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Check
                                                    type="switch"
                                                    id="open-ended-switch"
                                                    label="Definir Período da Agenda"
                                                    checked={!formData.open_ended}
                                                    onChange={(e) => setFormData({ ...formData, open_ended: !e.target.checked })}
                                                    className="mb-1"
                                                />
                                                <Form.Text className="text-muted small">
                                                    Se ativado, permite definir data de início e fim. Caso contrário, a agenda é válida indefinidamente.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Check
                                                    type="switch"
                                                    id="allow-holidays-switch"
                                                    label="Permitir Agendamento em Feriados"
                                                    checked={formData.allow_holidays}
                                                    onChange={(e) => setFormData({ ...formData, allow_holidays: e.target.checked })}
                                                    className="mb-1"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Check
                                                    type="switch"
                                                    id="validate-slots-switch"
                                                    label="Validar Horários Rigorosamente"
                                                    checked={formData.validate_slots}
                                                    onChange={(e) => setFormData({ ...formData, validate_slots: e.target.checked })}
                                                    className="mb-1"
                                                />
                                                <Form.Text className="text-muted small">
                                                    Se ativo, exige que agendamentos sigam exatamente os slots e duração.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                            <div className="d-flex justify-content-end mt-3">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={loading}
                                    className="d-flex align-items-center gap-2 px-4"
                                >
                                    <Save size={18} /> Salvar Agenda
                                </Button>
                            </div>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {
                formData.professional && (
                    <div className="mt-4">
                        <h5 className="mb-3">Pré-visualização da Agenda</h5>
                        <div style={{ height: '600px' }}>
                            <SchedulingCalendar
                                professionalId={parseInt(formData.professional)}
                                readOnly={true}
                                height="100%"
                            />
                        </div>
                    </div>
                )
            }
        </Container >
    );
};

export default AgendaForm;

